import {
	Avatar,
	Flex,
	Typography,
	Tag,
	BackgroundColors,
	BorderColors,
	BorderRadius,
	TextColors,
} from "@abel-technologies-as/components";
import { StyledLink } from "./style";
import { DhcTableData, DhcTableProps } from "./DhcTable.types";
import { useEffect, useState } from "react";
import { placeholderAvatar } from "@/shared/constants";
import { valueOrFallback } from "@/shared/utils";
import { TFunction } from "i18next";

export const renderColumns = ({ url, t }: DhcTableProps & { t: TFunction<"dhcs", undefined> }) => {
	const AvatarWithFallback = ({ imageUrl }: { imageUrl?: string }) => {
		const [isBroken, setIsBroken] = useState(false);

		useEffect(() => {
			if (!imageUrl) {
				setIsBroken(false);
				return;
			}
			const img = new Image();
			img.onload = () => setIsBroken(false);
			img.onerror = () => setIsBroken(true);
			img.src = imageUrl;
		}, [imageUrl]);

		return <Avatar size={24} imageUrl={!imageUrl || isBroken ? placeholderAvatar : imageUrl} />;
	};

	return [
		{
			field: "name",
			headerName: t("name"),
			width: 250,

			renderCell: (row: DhcTableData) => {
				return (
					<Flex mediaSm="justify-content: flex-end;" justifyContent={"flex-start"} gap={"12px"}>
						<AvatarWithFallback imageUrl={row.avatarUrl} />
						<Typography fontWeight={500} fontSize={"14px"} color={TextColors.Primary} lineHeight={"20px"}>
							<StyledLink to={`/${url}/${row.id}`}>{valueOrFallback(row.name)}</StyledLink>
						</Typography>
					</Flex>
				);
			},
		},
		{
			field: "clients",
			headerName: t("clients"),
			width: 100,
			renderCell: (row: DhcTableData) => (
				<Typography color={TextColors.Tertiary} fontWeight={400} fontSize="14px" lineHeight="20px">
					{valueOrFallback(row.clients)}
				</Typography>
			),
		},
		{
			field: "surveyScore",
			headerName: t("survey_score"),
			width: 150,
			renderCell: (row: DhcTableData) => {
				const surveyLow = typeof row.surveyLow === "number" ? row.surveyLow : 0;
				return (
					<Flex mediaSm="justify-content: flex-end;" justifyContent="flex-start" gap="6px">
						<Typography color={TextColors.Tertiary} fontWeight={400} fontSize="14px" lineHeight="20px">
							{valueOrFallback(row.surveyScore)}
						</Typography>
						{surveyLow > 0 && (
							<Tag
								borderRadius={`${BorderRadius.FULL}px`}
								padding={"2px 8px"}
								fontSize={"12px"}
								fontWeight={500}
								lineHeight={"18px"}
								text={`${surveyLow} ${t("low")}`}
								backgroundColor={BackgroundColors.WarningPrimary}
								borderColor={BorderColors.Warning}
								textColor={TextColors.Warning}
							/>
						)}
					</Flex>
				);
			},
		},
		{
			field: "clientWorkouts",
			headerName: t("client_workouts"),
			width: 150,
			renderCell: (row: DhcTableData) => {
				return (
					<Flex justifyContent="flex-start" gap="8px" alignItems="center">
						<Typography
							fontFamily={"Inter-Regular"}
							color={TextColors.Primary}
							lineHeight={"14px"}
							variant={"h3"}
							fontWeight={500}
							fontSize={"14px"}>
							{valueOrFallback(row?.clientWorkouts)}
						</Typography>
						{row?.clientWorkoutsPercentage ? (
							<Tag
								borderRadius={`${BorderRadius.FULL}px`}
								padding={"2px 8px"}
								fontSize={"12px"}
								fontWeight={500}
								lineHeight={"18px"}
								text={`${row?.clientWorkoutsPercentage}%`}
								backgroundColor={BackgroundColors.PrimaryAlt}
								borderColor={BorderColors.Secondary}
								textColor={TextColors.Secondary}></Tag>
						) : null}
					</Flex>
				);
			},
		},
		{
			field: "activities",
			headerName: t("less_than_3_activities_7_30d"),
			width: 200,
			renderCell: (row: DhcTableData) => {
				return (
					<Flex justifyContent="flex-start" gap="8px" alignItems="center">
						<Typography
							fontFamily={"Inter-Regular"}
							color={TextColors.Primary}
							lineHeight={"14px"}
							variant={"h3"}
							fontWeight={500}
							fontSize={"14px"}>
							{valueOrFallback(row.activities, "0/0")}
						</Typography>
					</Flex>
				);
			},
		},
		{
			field: "churn",
			headerName: t("churned_30_90_all_time"),
			width: 300,
			renderCell: (row: DhcTableData) => {
				return (
					<Flex justifyContent="flex-start" gap="8px" alignItems="center">
						<Typography
							fontFamily={"Inter-Regular"}
							color={TextColors.Primary}
							lineHeight={"14px"}
							variant={"h3"}
							fontWeight={500}
							fontSize={"14px"}>
							{valueOrFallback(row.churn)}
						</Typography>
					</Flex>
				);
			},
		},
		{
			field: "payout",
			headerName: t("payout"),
			width: 200,
			renderCell: () => {
				return "-";
				// <Flex justifyContent="flex-start" gap="8px" alignItems="center">
				// 	<Typography
				// 		fontFamily={"Inter-Regular"}
				// 		color={TextColors.Primary}
				// 		lineHeight={"14px"}
				// 		variant={"h3"}
				// 		fontWeight={500}
				// 		fontSize={"14px"}>
				// 		{valueOrFallback(row.payout)}
				// 	</Typography>
				// 	{row.payoutStatus && (
				// 		<Tag
				// 			borderRadius={`${BorderRadius.FULL}px`}
				// 			padding={"2px 8px"}
				// 			fontSize={"12px"}
				// 			fontWeight={500}
				// 			lineHeight={"18px"}
				// 			customStyles={css`
				// 				text-transform: capitalize;
				// 			`}
				// 			text={row.payoutStatus}
				// 			backgroundColor={BackgroundColors.SuccessPrimary}
				// 			borderColor={BorderColors.Success_200}
				// 			textColor={TextColors.Success_500}></Tag>
				// 	)}
				// </Flex>
			},
		},
	];
};
